/**
 * Same-page anchor scrolling for the Dose Formulations Hero.
 *
 * Two issues compound here: (1) on desktop (see base.css `.page-wrapper`
 * rules) `html`/`body` are locked to `overflow: hidden` and `.page-wrapper`
 * is the real scroll container, so native `#hash` navigation never scrolls
 * the page; (2) Shopify prefixes every rendered section wrapper id with its
 * template/group instance id (`template--<id>__<handle>` or
 * `sections--<id>__<handle>`), so a hand-authored `#shopify-section-<handle>`
 * link — the pattern Shopify's own docs recommend for section anchors —
 * never matches `getElementById` exactly. This intercepts same-page anchor
 * clicks, resolves the target with a prefix-tolerant lookup, and scrolls it
 * into view directly (which correctly walks nested scrollable ancestors).
 */
class DoseFormulationsHero extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || !this.contains(link)) return;

    const hash = link.getAttribute('href').slice(1);
    const target = hash ? this.resolveTarget(hash) : null;
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.pushState(null, '', `#${hash}`);
  };

  /**
   * @param {string} hash - The link's fragment, without the leading `#`.
   * @returns {Element | null}
   */
  resolveTarget(hash) {
    const exact = document.getElementById(hash);
    if (exact) return exact;

    const handle = hash.replace(/^shopify-section-/, '');
    return document.querySelector(`[id$="__${CSS.escape(handle)}"]`);
  }
}

if (!customElements.get('dose-formulations-hero')) {
  customElements.define('dose-formulations-hero', DoseFormulationsHero);
}
